import React from "react";

import { captureException } from "@sentry/browser";
import cx from "classnames";
import OrderedMap from "orderedmap";
import { baseKeymap } from "prosemirror-commands";
import { history, redo, undo } from "prosemirror-history";
import { InputRule } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { type MarkSpec, type NodeSpec, Node as ProseMirrorNode, Schema } from "prosemirror-model";
import { EditorState, Plugin, Selection, TextSelection, Transaction } from "prosemirror-state";
import { insertPoint, ReplaceStep } from "prosemirror-transform";
import { type EditorProps, EditorView } from "prosemirror-view";
import "prosemirror-view/style/prosemirror.css";
import uuid from "uuid";

import { hasOwnProperty } from "~/lib/js";
import * as perf from "~/lib/perf";

import { isAndroid } from "../utils";
import ClipboardDOMSerializer from "./clipboardSerializer";
import { Extension, type ExtensionNodeView, MarkExtension, NodeExtension } from "./extension";
import BlockID from "./plugins/blockid";
import ProseReactContextPlugin, { updateContextTr } from "./plugins/context";
import { inputRules } from "./plugins/inputRules";
import { PosBookmarkExtension } from "./plugins/posbookmark";
import ReactHost, { ReactHostContainer } from "./plugins/reactHost";

import styles from "./css/editor.global.module.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let devtools: any = null;
if (process.env.NODE_ENV !== "production") {
	import("prosemirror-dev-tools").then(mod => (devtools = mod));
}

/** Is the Samsung Android keyboard deleting the whole document?
 *
 * There seems to be a bug with the Android 13.0 Samsung keyboard (with Grammarly) causing the whole document
 * to be deleted. This looks at a transaction and returns true if it seems like that's happening here.
 * @see {@link https://github.com/facebook/react-native/issues/35936}
 */
function transactionContainsAndroidBug(transaction: Transaction) {
	return transaction.steps.some(
		(step, index) =>
			isAndroid &&
			transaction.docs[index].content.size > 3 &&
			step instanceof ReplaceStep &&
			step.from === 1 &&
			step.to === transaction.docs[index].content.size - 1 &&
			!step.slice,
	);
}

const GET_DEFAULT_PLUGINS = () => {
	const defaultPlugins: Plugin[] = [
		history(),
		keymap({ "Mod-z": undo, "Mod-y": redo, "Mod-shift-z": redo }),
		keymap(baseKeymap),
	];
	if (process.env.NODE_ENV !== "production" && devtools) {
		defaultPlugins.push(
			keymap({
				"Shift-Space": (
					state: EditorState,
					dispatch: ((tr: Transaction) => void) | undefined,
					view: EditorView | undefined,
				): boolean => {
					devtools.applyDevTools(view);
					return true;
				},
			}),
		);
	}
	return defaultPlugins;
};

interface EditorConfigOpts {
	nodes?: { [key: string]: NodeSpec };
	marks?: { [key: string]: MarkSpec };
	extensions?: Extension[];
	plugins?: Plugin[];
	extraEditorProps?: EditorProps;
}

/**
 * Configuration for a [[ProseReact]] editor. Constructing this config can be expensive, so should only be done once
 * per editor and cached.
 */
export class EditorConfig {
	extensions: Extension[];
	nodes: { [key: string]: NodeSpec };
	marks: { [key: string]: MarkSpec };
	plugins: Plugin[];
	schema: Schema;
	nodeViews: {
		[key: string]: ExtensionNodeView;
	};
	extraEditorProps?: EditorProps;

	get spec() {
		const { nodes, marks } = this.schema.spec;
		return {
			nodes:
				nodes instanceof OrderedMap
					? (() => {
							const data: { [key: string]: unknown } = {};
							nodes.forEach((k, v) => {
								data[k] = v;
							});
							return data;
						})()
					: nodes,
			marks:
				marks instanceof OrderedMap
					? (() => {
							const data: { [key: string]: unknown } = {};
							marks.forEach((k, v) => {
								data[k] = v;
							});
							return data;
						})()
					: marks,
		};
	}

	constructor(opts: EditorConfigOpts) {
		this.plugins = opts.plugins || [];
		this.extensions = opts.extensions || [];
		this.nodes = opts.nodes || {};
		this.marks = opts.marks || {};
		this.nodeViews = {};

		this.extensions.forEach(extension => {
			const { name } = extension;
			this.plugins.push(...extension.plugins);
			if (extension instanceof NodeExtension) {
				this.nodes[name] = extension.node;

				if (extension.view !== null) {
					if (name in this.nodeViews) {
						throw new Error(`NodeView for ${name} is already defined elsewhere.`);
					}
					this.nodeViews[name] = extension.view;
				}
			} else if (extension instanceof MarkExtension) {
				this.marks[name] = extension.mark;
			}
			if (extension.nodeViews) {
				Object.entries(extension.nodeViews).forEach(([key, nodeView]) => {
					if (key in this.nodeViews) {
						throw new Error(`NodeView for ${key} is already defined elsewhere.`);
					}
					this.nodeViews[key] = nodeView;
				});
			}
		});

		this.schema = new Schema({
			nodes: this.nodes,
			marks: this.marks,
		});

		this.extensions.forEach(extension => {
			let $type;
			if (extension instanceof NodeExtension) {
				$type = this.schema.nodes[extension.name];
			} else if (extension instanceof MarkExtension) {
				$type = this.schema.marks[extension.name];
			}
			const keys = extension.keys({
				$type: $type,
			});
			if (keys) {
				this.plugins.push(keymap(keys));
			}
		});

		if (opts.extraEditorProps) {
			this.extraEditorProps = opts.extraEditorProps;
		}
	}
}

interface ProseReactProps {
	className?: string;
	initialDoc?: ProseMirrorNode | { [key: string]: unknown };
	config: EditorConfig;
	onChange?: (
		editorState: EditorState,
		contentChanged: boolean,
		isRemoteChange: boolean,
		oldState?: EditorState,
	) => void;
	controlledDoc?: ProseMirrorNode;
	context?: { [key: string]: unknown };
	editable?: boolean;
	resetSelectionOnControlledDocChange?: boolean;
	lockedNote?: boolean;
	user?: {
		fullName: string;
		lastName: string;
		firstName: string;
	} & { [key: string]: unknown };
	enableReactHost?: boolean;
	focusOnMount?: boolean;
}

export class ProseReact extends React.Component<ProseReactProps> {
	editorView: EditorView | null = null;
	extensions: Extension[] = this.props.config.extensions;
	/**
	 * This is a _mutable_ list of `InputRule`s. We pass this to the inputRules
	 * plugin, and later add items to this array. This is because the time when
	 * we initialize the plugins is before the extensions receive the editorView,
	 * which led to `Extension.$type` being null, which broke some stuff.
	 */
	inputRules: InputRule[] = [];
	reactHostExt = new ReactHost();
	posBookmarkExt = new PosBookmarkExtension();

	containerRef: React.RefObject<HTMLDivElement> = React.createRef();

	editableFalse = () => false;

	private mountEditor() {
		const editorContainer = this.containerRef.current;
		if (!editorContainer) {
			return;
		}

		this.extensions.forEach(ext => {
			ext.editorContainer = editorContainer;
		});

		let initialDoc = this.props.controlledDoc || this.props.initialDoc;
		if (initialDoc && !(initialDoc instanceof ProseMirrorNode)) {
			initialDoc = ProseMirrorNode.fromJSON(this.props.config.schema, initialDoc);
		}

		// Initialize default plugins last, since order matters for
		// keybinding priority.
		// blockId plugin needs to be applied before collab
		const plugins = [
			new BlockID({
				key: "blockId",
				generateId() {
					return uuid.v4();
				},
			}).asPlugin(),
			ProseReactContextPlugin({ initialContext: this.props.context || {} }),
			...this.props.config.plugins,
			inputRules({ rules: this.inputRules }),
			...GET_DEFAULT_PLUGINS(),
		];

		if (this.props.enableReactHost) {
			plugins.splice(0, 0, ...this.reactHostExt.plugins);
		}
		plugins.push(...this.posBookmarkExt.plugins);

		const initialState = EditorState.create({
			schema: this.props.config.schema,
			doc: initialDoc || undefined,
			plugins: plugins,
		});

		this.extensions.forEach(ext => {
			ext.schema = this.props.config.schema;
		});

		if (!this.props.editable) {
			initialState.selection = Selection.atStart(initialState.doc);
		}

		const serializer = ClipboardDOMSerializer.fromSchema(this.props.config.schema);

		const id = uuid.v4();
		perf.mark(`prose:start-render:${id}`);

		let dispatching = false;

		const editorView = (this.editorView = new EditorView(editorContainer, {
			state: initialState,
			dispatchTransaction: transaction => {
				if (transactionContainsAndroidBug(transaction)) {
					const fakeError = new Error("Samsung Keyboard may be deleting the note");
					captureException(fakeError, {
						extra: {
							noteId: this.props.context?.["noteId"],
							transactionSteps: transaction.steps.length,
						},
					});
				}
				if (dispatching) {
					const serializableTransaction = {
						selectionSet: transaction.selectionSet,
						docChanged: transaction.docChanged,
						selection: transaction.selection.toJSON(),
						doc: transaction.doc.toJSON(),
						steps: transaction.steps.map(s => s.toJSON()),
						time: transaction.time,
						meta: {},
					};
					// Hack to make a Transaction serialize better in Sentry; the "meta"
					// attribute is an object without a prototype, which Sentry is unable
					// to serialize for some reason. Since it's a private property we
					// can't directly access it so we need to use hasOwnProperty to make
					// TypeScript happy.
					const erasedTransaction: {} = transaction;
					if (hasOwnProperty(erasedTransaction, "meta") && typeof erasedTransaction.meta === "object") {
						serializableTransaction.meta = { ...erasedTransaction.meta };
					}
					console.trace(
						"Nested dispatch detected. This may lead to undefined behavior in notes editor.",
						serializableTransaction,
					);
				}
				dispatching = true;
				// @ts-ignore  TODO: improve prose typing
				if (!editorView.docView) {
					console.warn(
						"Trying to dispatch transaction after editorView is destroyed.",
						JSON.parse(JSON.stringify(transaction)),
					);
					return;
				}
				const oldState = editorView.state;
				const newState = editorView.state.apply(transaction);
				editorView.updateState(newState);
				dispatching = false;
				if (this.props.onChange) {
					this.props.onChange(newState, transaction.docChanged, transaction.getMeta("remoteChange"), oldState);
				}
			},
			// We may need this line for iOS?
			// attributes: { autocapitalize: "off", autocomplete: "off", autocorrect: "off" },
			nodeViews: this.props.config.nodeViews,
			editable: this.props.editable === false ? this.editableFalse : undefined,
			clipboardSerializer: serializer,
			...this.props.config.extraEditorProps,
		}));
		if (this.props.onChange) {
			this.props.onChange(initialState, true, false);
		}

		editorView.extensions = new Map();

		[...this.extensions, this.reactHostExt, this.posBookmarkExt].forEach(extension => {
			extension.editor = this;
			extension.editorView = editorView;
			editorView.extensions.set(extension.name, extension);
			if (extension.inputRules) {
				this.inputRules.push(...extension.inputRules());
			}
			extension.init();
		});

		perf.mark(`prose:end-render:${id}`);
		perf.measure("prose:initial-render", `prose:start-render:${id}`, `prose:end-render:${id}`);
	}

	componentDidUpdate(prevProps: ProseReactProps) {
		if (this.editorView) {
			const state = this.editorView.state;
			const tr = state.tr;
			if (this.props.controlledDoc && this.props.controlledDoc !== prevProps.controlledDoc) {
				const previousSelection = tr.selection;
				tr.replaceWith(0, tr.doc.content.size, this.props.controlledDoc);
				if (this.props.resetSelectionOnControlledDocChange !== false) {
					tr.setSelection(Selection.atStart(tr.doc));
				} else {
					try {
						const newHead = insertPoint(tr.doc, previousSelection.head, state.schema.nodes["text"]);
						const newAnchor = insertPoint(tr.doc, previousSelection.anchor, state.schema.nodes["text"]);
						tr.setSelection(TextSelection.create(tr.doc, newAnchor || 0, newHead || 0));
					} catch {
						tr.setSelection(Selection.atStart(tr.doc));
					}
				}
			}

			if (this.props.context && this.props.context !== prevProps.context) {
				updateContextTr(this.props.context, tr);
			}

			this.editorView.updateState(state.apply(tr));

			if (prevProps.editable != this.props.editable) {
				this.editorView.setProps({ editable: this.props.editable === false ? this.editableFalse : undefined });
			}
		}
	}

	componentDidMount() {
		this.mountEditor();
		if (this.props.focusOnMount) {
			this.focus();
		}
	}

	componentWillUnmount() {
		this.extensions.forEach(extension => extension.destroy());
		this.posBookmarkExt.destroy();
		this.reactHostExt.destroy();
		this.editorView?.destroy();
	}

	focus() {
		this.editorView && this.editorView.focus();
	}

	/** Focus the editor, place the cursor at the end, and scroll it into view */
	focusToEnd() {
		if (!this.editorView) return;
		this.editorView.focus();
		let { tr } = this.editorView.state;
		tr.setSelection(Selection.atEnd(tr.doc));
		tr.scrollIntoView();
		this.editorView.dispatch(tr);
	}

	private onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
		this.extensions.forEach(extension => extension.onEditorBlur(e));
	};

	private onFocus = (e: React.FocusEvent<HTMLDivElement>) => {
		this.extensions.forEach(extension => extension.onEditorFocus(e));
	};

	render() {
		return (
			<>
				{this.props.enableReactHost && <ReactHostContainer reactHost={this.reactHostExt} />}
				<div
					ref={this.containerRef}
					className={cx(styles.ProseWrapper, this.props.className, { ProseLocked: this.props.lockedNote })}
					onBlur={this.onBlur}
					onFocus={this.onFocus}
					data-test-id="prose-react"
				/>
			</>
		);
	}
}

export default ProseReact;
